import type { DisputeCategory, DisputeStatus } from "@rivaled/shared";
import { AlertTriangle, Paperclip } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { trpc } from "../../lib/trpc";
import { uploadFile } from "../../lib/uploadFile";
import { cn } from "../../lib/utils";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Textarea } from "../ui";

type ExistingDispute = {
  id: string;
  status: DisputeStatus;
  withdrawnAt: Date | string | null;
};

type DisputeButtonProps = {
  existingDispute: ExistingDispute | null;
  filingWindowOpen: boolean;
  isLoading?: boolean;
  loadError?: string | null;
  matchId: string;
};

const DISPUTE_REASON_MIN_LENGTH = 20;
const MAX_EVIDENCE_FILES = 6;

const disputeCategoryOptions: Array<{
  value: DisputeCategory;
  label: string;
  hint: string;
}> = [
  {
    value: "wrong_result",
    label: "Wrong Result",
    hint: "Winner or score was reported incorrectly.",
  },
  {
    value: "cheating",
    label: "Cheating",
    hint: "Suspected exploit, smurfing, or unfair play.",
  },
  {
    value: "abandonment",
    label: "Abandonment",
    hint: "Players left early or match could not finish.",
  },
  {
    value: "technical",
    label: "Technical",
    hint: "Server crash, bug, or platform issue.",
  },
  {
    value: "other",
    label: "Other",
    hint: "Any issue that does not fit the listed categories.",
  },
];

export function DisputeButton({
  existingDispute,
  filingWindowOpen,
  isLoading = false,
  loadError = null,
  matchId,
}: DisputeButtonProps) {
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<DisputeCategory>("wrong_result");
  const [reason, setReason] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);

  const createMutation = trpc.dispute.create.useMutation();
  const addEvidenceMutation = trpc.dispute.addEvidence.useMutation();

  const isSubmitting = createMutation.isPending || addEvidenceMutation.isPending;
  const evidenceInputId = `dispute-evidence-input-${matchId}`;

  const blockReason = loadError
    ? "Could not load dispute status. Please refresh and try again."
    : isLoading
      ? "Loading dispute status..."
      : getBlockReason(existingDispute, filingWindowOpen);
  const canOpenModal = blockReason === null;

  const resetForm = () => {
    setCategory("wrong_result");
    setReason("");
    setEvidenceFiles([]);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < DISPUTE_REASON_MIN_LENGTH) {
      toast.error(`Please provide at least ${DISPUTE_REASON_MIN_LENGTH} characters of detail.`);
      return;
    }

    try {
      const created = await createMutation.mutateAsync({
        matchId,
        category,
        reason: trimmedReason,
      });

      let attachedCount = 0;
      let failedCount = 0;
      for (const file of evidenceFiles) {
        const kind = evidenceKindForMime(file.type);
        if (!kind) {
          toast.error(`Unsupported evidence type: ${file.name}`);
          continue;
        }

        try {
          const result = await uploadFile(file, "dispute-evidence", created.id, kind);

          await addEvidenceMutation.mutateAsync({
            disputeId: created.id,
            kind,
            storageKey: result.key,
            mimeType: file.type,
            sizeBytes: file.size,
          });

          attachedCount += 1;
        } catch {
          failedCount += 1;
        }
      }

      await utils.dispute.getByMatch.invalidate({ matchId });
      setOpen(false);
      resetForm();
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} attachment${failedCount === 1 ? "" : "s"} failed to upload. The dispute was still submitted.`,
        );
      }
      if (attachedCount > 0) {
        toast.success(
          `Dispute submitted with ${attachedCount} attachment${attachedCount === 1 ? "" : "s"}.`,
        );
      } else {
        toast.success("Dispute submitted.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to submit dispute");
    }
  };

  return (
    <section className="rounded-xl border border-surface-700/40 bg-surface-800/40 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-wide text-surface-100">
            Dispute Result
          </h3>
          <p className="mt-1 text-xs text-surface-400">
            File a dispute if the reported match result is wrong.
          </p>
          {blockReason ? <p className="mt-1 text-xs text-surface-500">{blockReason}</p> : null}
        </div>

        <Button
          variant={canOpenModal ? "primary" : "ghost"}
          disabled={!canOpenModal}
          onClick={() => setOpen(true)}
          className="sm:min-w-40"
        >
          <AlertTriangle className="mr-2 h-4 w-4" />
          {canOpenModal ? "Open Dispute Form" : "Unavailable"}
        </Button>
      </div>

      <Modal open={open} onClose={handleClose} className="!max-w-lg">
        <ModalHeader subtitle="Provide evidence so moderators can verify and resolve quickly.">
          File Match Dispute
        </ModalHeader>
        <ModalBody className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-surface-400">
              Category
            </p>
            <div className="grid grid-cols-2 gap-2">
              {disputeCategoryOptions.map((option) => {
                const selected = category === option.value;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={() => setCategory(option.value)}
                    className={cn(
                      "h-auto justify-start rounded-md border px-2.5 py-2 text-left text-xs font-semibold transition-colors",
                      selected
                        ? "border-brand-500/60 bg-brand-500/10 text-brand-300"
                        : "border-surface-700 text-surface-300 hover:border-surface-500 hover:text-surface-100",
                      isSubmitting && "cursor-not-allowed opacity-60",
                    )}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
            <p className="text-xs text-surface-500">
              {disputeCategoryOptions.find((option) => option.value === category)?.hint}
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-surface-400">
              Details
            </p>
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Describe what happened and why the result should be reviewed."
              rows={4}
              resize="none"
              disabled={isSubmitting}
            />
            <p className="text-xs text-surface-500">
              Minimum {DISPUTE_REASON_MIN_LENGTH} characters (
              {Math.max(0, DISPUTE_REASON_MIN_LENGTH - reason.trim().length)} remaining).
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor={evidenceInputId}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold",
                isSubmitting
                  ? "cursor-not-allowed border-surface-700 text-surface-500"
                  : "cursor-pointer border-surface-600 text-surface-300 hover:border-brand-500/40 hover:text-brand-300",
              )}
            >
              <Paperclip className="h-3.5 w-3.5" />
              Attach Evidence
            </label>
            <Input
              id={evidenceInputId}
              type="file"
              multiple
              accept="image/*,video/*,text/plain,application/json"
              className="hidden"
              wrapperClassName="hidden"
              disabled={isSubmitting}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length > MAX_EVIDENCE_FILES) {
                  toast.warning(
                    `You can attach up to ${MAX_EVIDENCE_FILES} files. Extra files were ignored.`,
                  );
                }
                setEvidenceFiles(files.slice(0, MAX_EVIDENCE_FILES));
                event.currentTarget.value = "";
              }}
            />
            {evidenceFiles.length > 0 ? (
              <p className="text-xs text-surface-400">
                {evidenceFiles.length} file(s):{" "}
                {evidenceFiles
                  .slice(0, 3)
                  .map((file) => file.name)
                  .join(", ")}
                {evidenceFiles.length > 3 ? " ..." : ""}
              </p>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Dispute"}
          </Button>
        </ModalFooter>
      </Modal>
    </section>
  );
}

function evidenceKindForMime(mimeType: string): "image" | "video" | "log" | null {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType === "text/plain" || mimeType === "application/json") return "log";
  return null;
}

function getBlockReason(
  existingDispute: ExistingDispute | null,
  filingWindowOpen: boolean,
): string | null {
  if (existingDispute) {
    if (existingDispute.status === "open") {
      return "A dispute is already open for this match.";
    }
    if (existingDispute.status === "under_review") {
      return "This dispute is already under moderator review.";
    }
    if (existingDispute.withdrawnAt) {
      if (!filingWindowOpen) {
        return "The dispute filing window has closed for this match.";
      }
      return null;
    }
    return "This match already has a resolved dispute.";
  }

  if (!filingWindowOpen) {
    return "The dispute filing window has closed for this match.";
  }

  return null;
}
